import * as React from 'react';

import { observer } from 'mobx-react';
import styled from 'styled-components';

import { ArrayMinOne } from '@estee/elc-universal-utils';
import {
    getMarketingPageViewRegistry,
    IMarketingPageFeature
} from '~views/marketing-page/registry';
import { serviceName } from '~setup/packageInfo';
import { Breakpoints, ContentSpacing } from '@estee/elc-base-theme';
import { ILoyaltyLayouts } from '~views/components/interfaces';
import { OrderRender } from '@estee/elc-layouts';

import { classes } from '~utils/Classes';
import { ILoyaltyMarketingPage } from '~exported/loyalty-marketing-page/ILoyaltyMarketingPage';
import { Spinner } from '@estee/elc-spinner';
import WithStyles from 'src/internal/hooks/WithStyles';

export const TEST_ID_LOYALTY_MARKETING = 'loyalty-marketing-page';
export const TEST_ID_LOYALTY_MARKETING_SPINNER = 'loyalty-marketing-spinner';
const loyaltyMarketingPageClass = classes('loyalty-marketing-page', 'brand-specific');
const loyaltyMarketingPageClassNonLoyalty = classes(
    'loyalty-marketing-page',
    'brand-specific',
    'non-loyalty-user'
);

export const loyaltyConfigs: ArrayMinOne<string> = ['loyaltyMarketingPageConfig'];

const LoyaltyMarketingPageWrapper = styled.div<ILoyaltyLayouts>`
    --desktop-max-width: 1920px;
    --container-column-gap: ${ContentSpacing.space16};
    --container-row-gap: ${ContentSpacing.space40};

    .fullbleed {
        grid-column: 1/-1;
    }

    display: grid;
    grid-auto-flow: dense;
    grid-template-areas: ${({ smallLayout }) => smallLayout};
    column-gap: 0;

    @media screen and (min-width: ${Breakpoints.desktop}px) {
        grid-template-areas: ${({ largeLayout }) => largeLayout};
        column-gap: var(--container-column-gap);
        grid-template-columns:
            calc(50% - (var(--desktop-max-width) / 2))
            repeat(6, 1fr)
            calc(50% - (var(--desktop-max-width) / 2));
    }
`;

const StyledSpinner = styled(Spinner)`
    width: 50px;
    height: 50px;
    margin: 100px auto;
`;

const MarketingSpinner = observer(() => (
    <div data-test-id={TEST_ID_LOYALTY_MARKETING}>
        <StyledSpinner isLoading />
    </div>
));

// eslint-disable-next-line @estee/elc-best-practice/uses-observer
@observer
class LoyaltyMarketingPage extends React.Component<ILoyaltyMarketingPage> {
    public static displayName: string;

    public componentDidMount = async () => {
        await this.props.viewController.loaded();
    };

    public render() {
        const { viewController } = this.props;
        const { isLoading, isLoyaltyUser, layouts, configs, isSignedIn } = viewController.data;
        const userSignedInClass = isSignedIn
            ? classes('user-state-logged-in')
            : classes('user-state-not-logged-in');
        const loyaltyClass = isLoyaltyUser
            ? loyaltyMarketingPageClass
            : loyaltyMarketingPageClassNonLoyalty;
        const loyaltyMarketingPageUserStateClass =  userSignedInClass
            ? `${userSignedInClass} ${loyaltyClass}`
            : loyaltyClass;
        const featureConfig: IMarketingPageFeature = {
            componentConfig: configs,
            controller: viewController
        };

        if (isLoading) {
            return <MarketingSpinner />;
        }

        return (
            <WithStyles>
                <LoyaltyMarketingPageWrapper
                    smallLayout={layouts.smallLayout}
                    largeLayout={layouts.largeLayout}
                    className={loyaltyMarketingPageUserStateClass}
                    data-test-id={TEST_ID_LOYALTY_MARKETING}
                >
                    <OrderRender
                        orderConfig={configs}
                        serviceName={serviceName}
                        viewRegistry={getMarketingPageViewRegistry(featureConfig)}
                    />
                </LoyaltyMarketingPageWrapper>
            </WithStyles>
        );
    }
}

LoyaltyMarketingPage.displayName = 'LoyaltyMarketingPage';

export default LoyaltyMarketingPage;
